/**
 * ButterBar - Toast-like notification with action button
 * Story 232: View history of changes and undo them
 * Story 464: Extended with generic actionLabel/onAction for export download
 *
 * Displays a dismissible notification with an optional action button.
 * Auto-dismisses after a configurable timeout.
 */

import { useEffect, useRef, useCallback } from 'react';

/** Default auto-dismiss time in milliseconds */
const AUTO_DISMISS_MS = 5000;

/** Maximum time undo is available (for UX consistency) */
const UNDO_TIMEOUT_MS = 10000;

interface ButterBarProps {
  /** Message to display */
  message: string;
  /** Called when undo is clicked (backward compat) */
  onUndo?: () => void;
  /** Called when action button is clicked (generic) */
  onAction?: () => void;
  /** Label for the action button (default: "Undo") */
  actionLabel?: string;
  /** Called when bar is dismissed (auto or manual) */
  onDismiss: () => void;
  /** Auto-dismiss timeout in ms (default 5000) */
  autoDismissMs?: number;
  /** Whether action is currently in progress */
  undoInProgress?: boolean;
}

export function ButterBar({
  message,
  onUndo,
  onAction,
  actionLabel,
  onDismiss,
  autoDismissMs = AUTO_DISMISS_MS,
  undoInProgress = false,
}: ButterBarProps) {
  const actionHandler = onAction || onUndo;
  const label = actionLabel || 'Undo';
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    };
  }, []);

  // Set up auto-dismiss timer
  useEffect(() => {
    // Start auto-dismiss timer
    timerRef.current = setTimeout(() => {
      onDismiss();
    }, autoDismissMs);

    // Also set up max undo timeout (hard limit)
    maxTimerRef.current = setTimeout(() => {
      onDismiss();
    }, UNDO_TIMEOUT_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    };
  }, [autoDismissMs, onDismiss]);

  // Handle action click - cancel timers and trigger action
  const handleAction = useCallback(() => {
    if (!actionHandler) return;
    // Cancel auto-dismiss
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    actionHandler();
  }, [actionHandler]);

  // Handle manual dismiss
  const handleDismiss = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (maxTimerRef.current) {
      clearTimeout(maxTimerRef.current);
      maxTimerRef.current = null;
    }
    onDismiss();
  }, [onDismiss]);

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 'var(--z-toast, 300)',
        backgroundColor: 'var(--ceetrix-900, #1e293b)',
        color: 'var(--text-on-dark, #f8fafc)',
        padding: '12px 20px',
        borderRadius: 'var(--radius-lg, 8px)',
        boxShadow: 'var(--shadow-lg, 0 10px 25px -5px rgba(0, 0, 0, 0.25))',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        maxWidth: '90%',
        minWidth: '300px',
        animation: 'butterbar-slide-up 0.2s ease-out',
      }}
    >
      {/* Message */}
      <span
        style={{
          flex: 1,
          fontSize: '14px',
          lineHeight: 1.4,
        }}
      >
        {message}
      </span>

      {/* Action button (Undo / Download / custom label) */}
      {actionHandler && (
        <button
          onClick={handleAction}
          disabled={undoInProgress}
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--ceetrix-400, #a5b4fc)',
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 'var(--radius-sm, 4px)',
            cursor: undoInProgress ? 'wait' : 'pointer',
            opacity: undoInProgress ? 0.6 : 1,
            transition: 'background-color 0.15s ease',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            if (!undoInProgress) {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {undoInProgress ? `${label}...` : label}
        </button>
      )}

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        aria-label="Dismiss"
        style={{
          padding: '4px',
          fontSize: '16px',
          color: 'var(--text-muted, #94a3b8)',
          backgroundColor: 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm, 4px)',
          cursor: 'pointer',
          lineHeight: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        &times;
      </button>

      {/* CSS animation */}
      <style>{`
        @keyframes butterbar-slide-up {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
